//* 지망생용 코치 컨텐츠 관련 API
//  2025.01.27
//  희문

import * as BaseApi from "api/Base.api";
import { IPagination } from "api/interfaces/common.interface";

//* 컨텐츠 목록 조회
//  2025.01.27
//  희문
export interface IFetchContentListParams {
    page?: number;
    limit?: number;
    koachId?: number; // 특정 코치의 콘텐츠만 조회할 경우
}

export interface IContentMultiText {
    id: number;
    contentId: number;
    userId?: string;
    languageCode: string;
    title?: string;
    desc: string;
    language?: {
        code: string;
        names: string;
    };
}

export interface IContent {
    id: number;
    userId: string;
    title?: string;
    desc?: string;
    youtubeUrl?: string;
    videoId?: string;
    isActivated: boolean;
    isFavorite?: boolean;
    favoriteCount?: number;
    reactionCount?: number;
    commentCount?: number;
    myReactionTypeCode?: string | null;
    createdAt: string;
    updatedAt: string;
    video?: {
        id: string;
        name: string;
        subUrl: string;
        thumbnailImageId?: string;
        isPublic?: boolean;
        thumbnailImage?: {
            id: string;
            name: string;
            subUrl: string;
            isPublic?: boolean;
        };
        thumbnailImageUrl?: string; // 이미지 URL
    };
    user?: {
        id: string;
        username: string;
        userKoach?: {
            id: number;
            nickname?: string;
            profileImageId?: string;
            profileImage?: {
                id: string;
                name: string;
                subUrl: string;
                isPublic?: boolean;
            };
            profileImageUrl?: string; // 프로필 이미지 URL (호환성)
            profileImageInfo?: {
                id: string;
                url: string;
            }; // 프로필 이미지 정보
        };
    };
    multiTexts?: IContentMultiText[];
}

export interface IFetchContentListResponse {
    contentList: IContent[];
    pagination: IPagination;
}

export const fetchContentList = async (params: IFetchContentListParams): Promise<IFetchContentListResponse> => {
    const url = "/student/v2/content";
    return await BaseApi.get(url, params);
};

//* 컨텐츠 상세 조회
//  2025.01.27
//  희문
export interface IFetchContentDetailResponse {
    content: IContent;
}

export const fetchContentDetail = async (id: number): Promise<IFetchContentDetailResponse> => {
    const url = `/student/v2/content/${id}`;
    return await BaseApi.get(url);
};

//* 컨텐츠 즐겨찾기 토글
//  2025.01.27
//  희문
export interface IToggleContentFavoriteResponse {
    isFavorite: boolean;
    favoriteCount: number;
    message: string;
}

export const toggleContentFavorite = async (id: number): Promise<IToggleContentFavoriteResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/content/${id}/favorite`);
        console.log("콘텐츠 즐겨찾기 토글 성공:", response);
        return response as IToggleContentFavoriteResponse;
    } catch (error) {
        console.error("콘텐츠 즐겨찾기 토글 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 목록 조회
//  2025.01.27
//  희문
export interface IContentComment {
    id: number;
    contentId: number;
    userId: string;
    parentCommentId?: number;
    text: string;
    contents?: string; // CommentItem 호환성
    likeCount: number;
    isLiked?: boolean;
    createdAt: string;
    updatedAt: string;
    user?: any;
    childList?: IContentComment[];
}

export interface IContentCommentsResult {
    commentList: IContentComment[];
    pagination: IPagination;
}

export const getContentComments = async (contentId: number, params?: {
    page?: number;
    limit?: number;
}): Promise<IContentCommentsResult> => {
    try {
        const response = await BaseApi.get(`/student/v2/content/${contentId}/comment`, params);
        return response as IContentCommentsResult;
    } catch (error) {
        console.error("콘텐츠 댓글 목록 조회 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 작성
//  2025.01.27
//  희문
export interface ICreateContentCommentRequest {
    text: string;
    parentId?: number;
}

export interface ICreateContentCommentResponse {
    comment: IContentComment;
}

export const createContentComment = async (contentId: number, text: string, parentId?: number): Promise<ICreateContentCommentResponse> => {
    try {
        const data: ICreateContentCommentRequest = {
            text: text,
            parentId: parentId
        };
        const response = await BaseApi.post2(`/student/v2/content/${contentId}/comment`, data);
        return response as ICreateContentCommentResponse;
    } catch (error) {
        console.error("콘텐츠 댓글 작성 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 삭제
//  2025.01.27
//  희문
export const deleteContentComment = async (contentId: number, commentId: number): Promise<void> => {
    try {
        await BaseApi.remove(`/student/v2/content/${contentId}/comment/${commentId}`);
    } catch (error) {
        console.error("콘텐츠 댓글 삭제 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 좋아요 토글
//  2025.01.27
//  희문
export interface IToggleContentCommentLikeResponse {
    isLiked: boolean;
    likeCount: number;
    message: string;
}

export const toggleContentCommentLike = async (contentId: number, commentId: number): Promise<IToggleContentCommentLikeResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/content/${contentId}/comment/${commentId}/like`);
        return response as IToggleContentCommentLikeResponse;
    } catch (error) {
        console.error("콘텐츠 댓글 좋아요 토글 실패:", error);
        throw error;
    }
};

//* 콘텐츠 리액션 토글
//  2025.01.27
//  희문
export interface IToggleContentReactionRequest {
    reactionTypeCode: string;
}

export interface IToggleContentReactionResponse {
    isReactionActive: boolean;
    reactionTypeCode: string | null;
    reactionCount: number;
    message: string;
}

export const toggleContentReaction = async (contentId: number, reactionTypeCode: string): Promise<IToggleContentReactionResponse> => {
    try {
        const data: IToggleContentReactionRequest = {
            reactionTypeCode: reactionTypeCode
        };
        const response = await BaseApi.post2(`/student/v2/content/${contentId}/reaction`, data);
        return response as IToggleContentReactionResponse;
    } catch (error) {
        console.error("콘텐츠 리액션 토글 실패:", error);
        throw error;
    }
};
